import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, Facebook, Instagram, MapPin, ChevronDown } from 'lucide-react';
import { MobileNav } from './MobileNav';
const NAV_LINKS = [{
  name: 'Home',
  path: '/'
}, {
  name: 'About',
  path: '/About.html'
}, {
  name: 'Services',
  path: '/interior-design-services'
}, {
  name: 'Portfolio',
  path: '/Portfolio.html'
}, {
  name: 'Reviews',
  path: '/Testimonials.html'
}, {
  name: 'Retail Store',
  path: '/RetailStoreInformation.html',
  submenu: [{
    name: 'Visit Retail Store',
    path: '/RetailStoreInformation.html'
  }, {
    name: 'Previously Loved Boutique',
    path: '/previously-loved-boutique'
  }]
}, {
  name: 'Contact',
  path: '/ContactUs.html'
}];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <>
      {/* Eyebrow Bar - Above Header */}
      <div className="bg-cream-100 border-b border-taupe-200 py-2 px-4" data-id="element-88">
        <div className="max-w-7xl mx-auto text-center" data-id="element-89">
          <p className="text-xs md:text-sm text-charcoal-900/70 tracking-wide" data-id="element-90">
            Interior Design • Home Décor • Staging — Michigan & Southwest
            Florida
          </p>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-cream-100/95 backdrop-blur-md shadow-soft py-1' : 'bg-cream-100 py-1.5'}`} data-id="element-91">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-92">
          <div className="flex items-center justify-between gap-4" data-id="element-93">
            {/* Logo */}
            <Link to="/" className="relative z-50 flex-shrink-0 py-1" data-id="element-94">
              <img src="https://cdn.magicpatterns.com/uploads/cPGepR4tzfa8FoK3BVJiwu/dark-logo2.png" alt="Down Home Interiors" className="h-full max-h-14 w-auto object-contain" data-id="element-95" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-6" data-id="element-96">
              {NAV_LINKS.map(link => <div key={link.path} className="relative group" data-id="element-97">
                  {link.submenu ? <>
                      <button className={`text-sm tracking-wide uppercase font-medium transition-colors hover:text-sage-500 flex items-center gap-1 ${location.pathname === link.path || link.submenu.some(sub => sub.path === location.pathname) ? 'text-sage-500' : 'text-charcoal-900'}`} data-id="element-98">
                        {link.name}
                        <ChevronDown size={14} data-id="element-99" />
                      </button>
                      <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-100">
                        <div className="bg-white border border-taupe-200 shadow-soft min-w-[220px] py-2" data-id="element-101">
                          {link.submenu.map(sublink => <Link key={sublink.path} to={sublink.path} className={`block px-4 py-2 text-sm hover:bg-cream-50 transition-colors ${location.pathname === sublink.path ? 'text-sage-500 bg-cream-50' : 'text-charcoal-900'}`} data-id="element-102">
                              {sublink.name}
                            </Link>)}
                        </div>
                      </div>
                    </> : <Link to={link.path} className={`text-sm tracking-wide uppercase font-medium transition-colors hover:text-sage-500 ${location.pathname === link.path ? 'text-sage-500' : 'text-charcoal-900'}`} data-id="element-103">
                      {link.name}
                    </Link>}
                </div>)}
            </nav>

            {/* Contact Info & Social - Desktop Only - Stacked */}
            <div className="hidden lg:flex flex-col items-end gap-2 flex-shrink-0" data-id="element-104">
              <div className="flex items-center gap-3" data-id="element-105">
                <a href="tel:5175455955" className="flex items-center gap-2 text-sm text-charcoal-900 hover:text-sage-500 transition-colors whitespace-nowrap" data-id="element-106">
                  <Phone size={16} data-id="element-107" />
                  <span data-id="element-108">(517) 545-5955</span>
                </a>
                <a href="https://www.google.com/maps/search/?api=1&query=2729+E.+Grand+River+Ave.+Howell+MI+48843" target="_blank" rel="noopener noreferrer" className="text-charcoal-900 hover:text-sage-500 transition-colors" aria-label="Get Directions" data-id="element-109">
                  <MapPin size={16} data-id="element-110" />
                </a>
              </div>
              <div className="flex items-center gap-3" data-id="element-111">
                <Link to="/JoinEmailList.html" className="text-xs text-charcoal-900 hover:text-sage-500 transition-colors uppercase tracking-wide" data-id="element-112">
                  Join Email List
                </Link>
                <span className="text-charcoal-900/30" data-id="element-113">|</span>
                <a href="https://www.facebook.com/DownTowneApparel/" target="_blank" rel="noopener noreferrer" className="text-charcoal-900 hover:text-sage-500 transition-colors" aria-label="Facebook" data-id="element-114">
                  <Facebook size={18} data-id="element-115" />
                </a>
                <a href="https://www.instagram.com/downhomeinteriors/" target="_blank" rel="noopener noreferrer" className="text-charcoal-900 hover:text-sage-500 transition-colors" aria-label="Instagram" data-id="element-116">
                  <Instagram size={18} data-id="element-117" />
                </a>
              </div>
            </div>

            {/* Mobile Menu Trigger */}
            <button className="lg:hidden p-2 text-charcoal-900 hover:text-sage-500 transition-colors" onClick={() => setIsMobileOpen(true)} aria-label="Open menu" data-id="element-118">
              <Menu size={28} data-id="element-119" />
            </button>
          </div>
        </div>
      </header>

      <MobileNav isOpen={isMobileOpen} onClose={() => setIsMobileOpen(false)} links={NAV_LINKS} data-id="element-120" />
    </>;
}